import { Box, useTheme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';
import { ActionPressable } from '../shared/ActionPressable';
import { ActionIconSlot, ActionLabel, ActionSpinner } from '../shared/ActionContent';
import { buttonIconSize, buttonPadding } from '../shared/button-presentation';
import { Button, type ButtonProps } from './Button';

export interface ButtonDestructiveProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary';
}

export function ButtonDestructive({ variant = 'primary', ...props }: ButtonDestructiveProps) {
  if (variant === 'primary') return <Button {...props} variant="destructive" />;
  return <SecondaryDestructiveButton {...props} />;
}

function SecondaryDestructiveButton({
  label,
  size = 'medium',
  fullWidth,
  leadingIcon,
  trailingIcon,
  accessibilityLabel = label,
  inverted: _inverted,
  ...props
}: Omit<ButtonProps, 'variant'>) {
  const theme = useTheme();
  const color = theme.colors['text.error'];
  const iconSize = Number(theme.tokens.sizing[buttonIconSize[size]]);
  return (
    <ActionPressable
      {...props}
      accessibilityLabel={accessibilityLabel}
      fullWidth={fullWidth}
      minHeight={Number(theme.tokens.sizing[`sizing.component.button.height.${size}`])}
      paddingHorizontal={theme.spacing[buttonPadding[size]]}
      paddingVertical={theme.spacing['spacing.general.x1']}
      appearance={({ pressed, hovered, disabled }) => ({
        borderColor: theme.colors['border.error'],
        borderWidth: theme.borderWidths[pressed || hovered ? 'border-width.emphasized' : 'border-width.standard'],
        overlayColor:
          pressed || hovered ? theme.colors[pressed ? 'state-layer.press' : 'state-layer.hover'] : undefined,
        opacity: disabled ? INTERACTION.opacity['040'] : 1,
      })}
    >
      {({ loading }) => (
        <>
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            maxWidth="100%"
            style={{ opacity: loading ? 0 : 1 }}
            aria-hidden={loading}
          >
            <ActionIconSlot icon={leadingIcon} size={iconSize} color={color} />
            <ActionLabel
              label={label}
              color={color}
              small={size !== 'large'}
              padding={size === 'small' ? 'spacing.general.x1' : 'spacing.general.x2'}
            />
            <ActionIconSlot icon={trailingIcon} size={iconSize} color={color} />
          </Box>
          {loading ? (
            <Box position="absolute" top={0} bottom={0} left={0} right={0} alignItems="center" justifyContent="center">
              <ActionSpinner color={theme.colors['border.error']} size={size === 'large' ? 'large' : 'small'} />
            </Box>
          ) : null}
        </>
      )}
    </ActionPressable>
  );
}
